import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import styles from "./ProfessionalCustomerDetail.module.css";
import AddProfessionalCustomerModal from "../../../../../components/Customers/AddProfessionalCustomerModal";
import ContactModal from "../../../../../components/Customers/ContactModal";
import { useProfessionalCustomerById, useUpdateProfessionalCustomer } from "../../../../../api/people/usePeopleApi";

const mapApiToUi = (api, fromState = {}) => {
  if (!api) return null;
  const addr = {
    street: api.street ?? "",
    city: api.city ?? "",
    zip: api.postalCode ?? "",
    country: api.country ?? "",
  };
  const financials = {
    totalSpent: parseFloat(api.totalSpent ?? 0),
    openBalance: parseFloat(api.openBalance ?? 0),
    creditNotes: fromState.financials?.creditNotes ?? 0,
    paymentTerms: fromState.financials?.paymentTerms ?? "30 days",
    lastPayment: fromState.financials?.lastPayment ?? "",
    lastPaymentAmount: fromState.financials?.lastPaymentAmount ?? 0,
  };
  const primaryContact = {
    id: 1,
    isPrimary: true,
    firstName: api.primaryContactFirstName ?? "",
    lastName: api.primaryContactLastName ?? "",
    email: api.primaryContactEmail ?? "",
    phone: api.primaryContactPhone ?? "",
    role: api.primaryContactRole ?? "",
    language: api.preferredLanguage ?? "",
  };
  return {
    id: api.id,
    ref: api.customerCode ?? api.id,
    primaryContact: {
      name: `${primaryContact.firstName} ${primaryContact.lastName}`.trim() || "—",
      email: primaryContact.email,
      phone: primaryContact.phone,
      role: primaryContact.role,
      language: primaryContact.language,
    },
    createdAt: api.createdAt
      ? new Date(api.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "",
    companyName: api.tradingName ?? "",
    legalName: api.legalName ?? "",
    vatNumber: api.vatNumber ?? "",
    industry: api.industry ?? "",
    website: api.website ?? "",
    country: api.country ?? "",
    city: api.city ?? "",
    status: api.status === "ACTIVE" ? "Active" : "Inactive",
    address: addr,
    invoicing: {
      email: api.invoiceEmail ?? "",
      address: api.invoicingAddress ?? "",
      paymentTerms: api.paymentTerms ? `${api.paymentTerms}` : "30",
    },
    contacts: [primaryContact],
    financials,
    totalBookings: api.totalBookings ?? 0,
    bookings: fromState.bookings ?? [],
    creditNotes: fromState.creditNotes ?? [],
    notes: [],
  };
};

// ── Constants ──────────────────────────────────────────────────────────────
const TYPE_STYLES = {
  Package: { bg: "#eff6ff", color: "#2563eb" },
  Flight:  { bg: "#f0fdf4", color: "#16a34a" },
  Hotel:   { bg: "#fdf4ff", color: "#9333ea" },
};
const TYPE_ICON = { Package: "📦", Flight: "✈️", Hotel: "🏨" };

// ── Sub-components ─────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div className={styles.infoRow}>
    <span className={styles.infoLabel}>{label}</span>
    <span className={styles.infoValue}>{value || "—"}</span>
  </div>
);

const StatCard = ({ label, value, sub, accent }) => (
  <div className={`${styles.statCard} ${accent ? styles.statCardAccent : ""}`}>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
    {sub && <div className={styles.statSub}>{sub}</div>}
  </div>
);

// ── Component ──────────────────────────────────────────────────────────────
const ProfessionalCustomerDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ref: customerRef } = useParams();

  const { data: apiCompany, loading, error, refetch } = useProfessionalCustomerById(customerRef);
  const { execute: updateCompany, error: updateError, clearError: clearUpdateError } = useUpdateProfessionalCustomer();

  const fromState = location.state?.company ?? {};
  const company = useMemo(() => {
    const mapped = mapApiToUi(apiCompany, fromState);
    if (!mapped) return null;
    return {
      ...mapped,
      bookings: fromState.bookings ?? mapped.bookings ?? [],
      creditNotes: fromState.creditNotes ?? mapped.creditNotes ?? [],
    };
  }, [apiCompany, fromState.bookings, fromState.creditNotes]);

  const c = company;

  const [activeTab,       setActiveTab]       = useState(location.state?.defaultTab ?? "overview");
  const [noteText,        setNoteText]        = useState("");
  const [localNotes,      setLocalNotes]      = useState([]);
  const [localContacts,   setLocalContacts]   = useState([]);
  const [primaryOverride, setPrimaryOverride] = useState(null);
  const [editModalOpen,   setEditModalOpen]   = useState(false);

  useEffect(() => {
    setLocalNotes(c?.notes ?? []);
  }, [c?.notes]);

  const [contactModal, setContactModal] = useState({ open: false, contact: null });
  const openAddContact  = ()   => setContactModal({ open: true,  contact: null });
  const openEditContact = (ct) => setContactModal({ open: true,  contact: ct   });
  const closeContact    = ()   => setContactModal({ open: false, contact: null });

  const contacts = useMemo(() => {
    if (!c) return [];
    const primary = primaryOverride ?? c.contacts?.[0] ?? null;
    const all = primary ? [primary, ...localContacts] : localContacts;
    return all.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
  }, [c?.contacts, primaryOverride, localContacts]);

  const handleSaveContact = (payload) => {
    if (contactModal.contact) {
      if (contactModal.contact.id === 1) {
        setPrimaryOverride({ ...payload, id: 1, isPrimary: true });
      } else {
        setLocalContacts(prev =>
          prev.map(ct => ct.id === payload.id ? { ...ct, ...payload } : ct)
        );
      }
    } else {
      setLocalContacts(prev => [
        ...prev,
        { ...payload, id: Date.now(), isPrimary: false },
      ]);
    }
    closeContact();
  };

  const handleBack      = () => navigate("/people/customers/professional");
  const handleEditOpen  = () => { clearUpdateError?.(); setEditModalOpen(true); };
  const handleEditClose = () => { clearUpdateError?.(); setEditModalOpen(false); };

  const handleEditSave = async (payload) => {
    if (!c?.ref) return;
    try {
      await updateCompany(c.ref, payload);
      setEditModalOpen(false);
      refetch();
    } catch (err) {
      console.error("Update company failed:", err);
    }
  };

  const handleSetPrimary = (contactId) => {
    if (contactId === 1) {
      setPrimaryOverride(prev => (prev ?? c?.contacts?.[0]) ? { ...(prev ?? c.contacts[0]), isPrimary: true } : null);
      setLocalContacts(prev => prev.map(ct => ({ ...ct, isPrimary: false })));
    } else {
      const promoted = localContacts.find(ct => ct.id === contactId);
      if (!promoted) return;
      const demoted = (primaryOverride ?? c?.contacts?.[0]) ? { ...(primaryOverride ?? c.contacts[0]), isPrimary: false } : null;
      setPrimaryOverride({ ...promoted, isPrimary: true });
      setLocalContacts(prev => [demoted, ...prev.filter(ct => ct.id !== contactId)].filter(Boolean));
    }
  };

  const handleRemoveContact = (contactId) => {
    setLocalContacts(prev => prev.filter(ct => ct.id !== contactId));
  };

  if (customerRef && loading && !apiCompany) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}>⏳</div>
          <p>Loading company...</p>
        </div>
      </div>
    );
  }

  if (customerRef && error && !apiCompany) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p>{error}</p>
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
          <button className={styles.backBtn} onClick={handleBack}>← Back</button>
        </div>
      </div>
    );
  }

  if (!c) return null;

  const companyInitials = (c.companyName || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";

  const TABS = [
    { key: "overview",   label: "Overview" },
    { key: "contacts",   label: "Contacts",   count: contacts.length },
    { key: "bookings",   label: "Bookings",   count: c.bookings?.length ?? c.totalBookings ?? 0 },
    { key: "financials", label: "Financials" },
    { key: "notes",      label: "Notes",      count: localNotes.length },
  ];

  return (
    <div className={styles.container}>

      {/* ── Breadcrumb ── */}
      <div className={styles.breadcrumb}>
        <button className={styles.backBtn} onClick={handleBack}>← Professional Customers</button>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>{c.companyName}</span>
      </div>

      {/* ── Profile Header ── */}
      <div className={styles.profileHeader}>
        <div className={styles.profileLeft}>
          <div className={styles.avatarLarge}>{companyInitials}</div>
          <div className={styles.profileInfo}>
            <div className={styles.profileName}>{c.companyName}</div>
            <div className={styles.profileLegal}>{c.legalName}</div>
            <div className={styles.profileRef}>{c.ref}</div>
            <div className={styles.profileMeta}>
              <span>🏦 {c.vatNumber}</span>
              <span className={styles.metaDot}>·</span>
              <span>🏭 {c.industry}</span>
              <span className={styles.metaDot}>·</span>
              <span>📍 {c.city}, {c.country}</span>
              <span className={styles.metaDot}>·</span>
              <span>Since {c.createdAt}</span>
            </div>
          </div>
        </div>
        <div className={styles.profileActions}>
          <span className={`${styles.statusBadge} ${c.status === "Active" ? styles.statusActive : styles.statusInactive}`}>
            <span className={styles.statusDot} />
            {c.status}
          </span>
          <button className={styles.editBtn} onClick={handleEditOpen}>✏️ Edit</button>
          <button
            className={styles.addBookingBtn}
            onClick={() => {
              const primary = contacts.find(ct => ct.isPrimary) ?? contacts[0];
              navigate("/bookings/add", {
                state: {
                  prefill: {
                    customerType: "Company",
                    customer: {
                      id:            c.id,
                      ref:           c.ref,
                      name:          c.companyName,
                      email:         primary?.email ?? c.invoicing?.email ?? "",
                      phone:         primary?.phone ?? "",
                      type:          "Company",
                      country:       c.country ?? "",
                      totalBookings: c.totalBookings ?? c.bookings?.length ?? 0,
                      contactName:   primary ? `${primary.firstName} ${primary.lastName}` : undefined,
                    },
                  },
                },
              });
            }}
          >
            + New Booking
          </button>
        </div>
      </div>

      {/* ── Quick Stats ── */}
      <div className={styles.statsRow}>
        <StatCard label="Total Bookings" value={c.totalBookings ?? c.bookings?.length ?? 0} sub="All time" />
        <StatCard
          label="Total Spent"
          value={`€${(c.financials?.totalSpent ?? 0).toLocaleString("en-EU", { minimumFractionDigits: 2 })}`}
          sub="All time"
        />
        <StatCard
          label="Open Balance"
          value={`€${(c.financials?.openBalance ?? 0).toLocaleString("en-EU", { minimumFractionDigits: 2 })}`}
          sub="Outstanding"
          accent={(c.financials?.openBalance ?? 0) > 0}
        />
        <StatCard
          label="Credit Notes"
          value={`€${(c.financials?.creditNotes ?? 0).toLocaleString("en-EU", { minimumFractionDigits: 2 })}`}
          sub="Available"
        />
      </div>

      {/* ── Tab Bar ── */}
      <div className={styles.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={`${styles.tabBadge} ${activeTab === tab.key ? styles.tabBadgeActive : ""}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Tab: Overview                                                   */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <div className={styles.tabContent}>
          <div className={styles.twoCol}>

            {/* Company Information */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>🏢</span>
                <h3>Company Information</h3>
                <button className={styles.cardEditBtn} onClick={handleEditOpen}>Edit</button>
              </div>
              <InfoRow label="Trading Name"  value={c.companyName} />
              <InfoRow label="Legal Name"    value={c.legalName} />
              <InfoRow label="VAT Number"    value={c.vatNumber} />
              <InfoRow label="Industry"      value={c.industry} />
              <InfoRow label="Website"       value={c.website} />
              <InfoRow label="Registered"    value={c.createdAt} />
            </div>

            {/* Address & Invoicing */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>🧾</span>
                <h3>Address & Invoicing</h3>
                <button className={styles.cardEditBtn} onClick={handleEditOpen}>Edit</button>
              </div>
              <InfoRow label="Street"          value={c.address?.street} />
              <InfoRow label="City"            value={[c.address?.city, c.address?.zip].filter(Boolean).join(", ") || undefined} />
              <InfoRow label="Country"         value={c.address?.country} />
              <div className={styles.infoSeparator} />
              <InfoRow label="Invoice Email"   value={c.invoicing?.email} />
              <InfoRow label="Payment Terms"   value={c.invoicing?.paymentTerms ? `${c.invoicing.paymentTerms} days` : undefined} />
            </div>

          </div>

          {/* Primary Contact Preview */}
          <div className={styles.card} style={{ marginTop: 14 }}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>👥</span>
              <h3>Contacts</h3>
              <span className={styles.cardCount}>{contacts.length} contact{contacts.length !== 1 ? "s" : ""}</span>
              <button className={styles.cardLinkBtn} onClick={() => setActiveTab("contacts")}>
                Manage all →
              </button>
            </div>
            <div className={styles.contactsPreview}>
              {contacts.slice(0, 3).map(ct => (
                <div key={ct.id} className={styles.contactPreviewRow}>
                  <div className={styles.contactAvatar}>
                    {(ct.firstName?.[0] ?? "")}{(ct.lastName?.[0] ?? "")}
                  </div>
                  <div className={styles.contactInfo}>
                    <div className={styles.contactName}>
                      {ct.firstName} {ct.lastName}
                      {ct.isPrimary && <span className={styles.primaryBadge}>Primary</span>}
                    </div>
                    <div className={styles.contactRole}>{ct.role}</div>
                  </div>
                  <div className={styles.contactContact}>
                    <div>{ct.email}</div>
                    <div className={styles.contactPhone}>{ct.phone}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Bookings Preview */}
          <div className={styles.card} style={{ marginTop: 14 }}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>🗂️</span>
              <h3>Recent Bookings</h3>
              <button className={styles.cardLinkBtn} onClick={() => setActiveTab("bookings")}>
                View all →
              </button>
            </div>
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>Ref</th><th>Type</th><th>Service Dates</th>
                  <th>Pax</th><th>Price</th><th>Payment</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(c.bookings ?? []).slice(0, 3).map(b => {
                  const ts = TYPE_STYLES[b.type] || {};
                  return (
                    <tr key={b.ref}>
                      <td><span className={styles.miniRef}>{b.ref}</span></td>
                      <td>
                        <span className={styles.miniTypeBadge} style={{ background: ts.bg, color: ts.color }}>
                          {TYPE_ICON[b.type]} {b.type}
                        </span>
                      </td>
                      <td>
                        <span className={styles.miniDate}>{b.firstService}</span>
                        {b.lastService !== b.firstService && (
                          <span className={styles.miniDateSub}> → {b.lastService}</span>
                        )}
                      </td>
                      <td><span className={styles.paxBadge}>{b.pax} pax</span></td>
                      <td><strong>€{b.totalPrice.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</strong></td>
                      <td>
                        <span className={`${styles.miniPayBadge} ${
                          b.paymentStatus === "Paid"    ? styles.payPaid    :
                          b.paymentStatus === "Partial" ? styles.payPartial : styles.payUnpaid}`}>
                          {b.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.miniStatusBadge} ${
                          b.bookingStatus === "Confirmed" ? styles.statusConfirmed :
                          b.bookingStatus === "Pending"   ? styles.statusPending   : styles.statusCancelled}`}>
                          {b.bookingStatus}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "contacts" && (
        <div className={styles.tabContent}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>👥</span>
              <h3>Company Contacts</h3>
              <span className={styles.cardCount}>{contacts.length} contact{contacts.length !== 1 ? "s" : ""}</span>
              <button
                className={styles.addContactBtn}
                onClick={openAddContact}
              >
                + Add Contact
              </button>
            </div>

            <div className={styles.contactsRule}>
              <span>⚠️</span>
              One contact must always be marked as primary. Multiple contacts can be added per company.
            </div>

            <div className={styles.contactsList}>
              {contacts.map(ct => (
                <div key={ct.id} className={`${styles.contactCard} ${ct.isPrimary ? styles.contactCardPrimary : ""}`}>
                  <div className={styles.contactCardLeft}>
                    <div className={`${styles.contactAvatarLg} ${ct.isPrimary ? styles.contactAvatarPrimary : ""}`}>
                      {(ct.firstName?.[0] ?? "")}{(ct.lastName?.[0] ?? "")}
                    </div>
                    <div>
                      <div className={styles.contactCardName}>
                        {ct.firstName} {ct.lastName}
                        {ct.isPrimary && <span className={styles.primaryBadge}>⭐ Primary</span>}
                      </div>
                      <div className={styles.contactCardRole}>{ct.role}</div>
                      <div className={styles.contactCardLang}>🌐 {ct.language}</div>
                    </div>
                  </div>
                  <div className={styles.contactCardMid}>
                    <div className={styles.contactCardEmail}>📧 {ct.email}</div>
                    <div className={styles.contactCardPhone}>📞 {ct.phone}</div>
                  </div>
                  <div className={styles.contactCardActions}>
                    {!ct.isPrimary && (
                      <button
                        className={styles.btnSetPrimary}
                        onClick={() => handleSetPrimary(ct.id)}
                      >
                        Set as Primary
                      </button>
                    )}
                    <button className={styles.btnEditContact} onClick={() => openEditContact(ct)}>Edit</button>
                    {!ct.isPrimary && (
                      <button
                        className={styles.btnRemoveContact}
                        onClick={() => handleRemoveContact(ct.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Tab: Bookings                                                   */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeTab === "bookings" && (
        <div className={styles.tabContent}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>🗂️</span>
              <h3>All Bookings</h3>
              <span className={styles.cardCount}>{(c.bookings ?? []).length} total</span>
            </div>
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>Ref</th><th>Type</th><th>First Service</th><th>Last Service</th>
                  <th>Pax</th><th>Price</th><th>Payment</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {(c.bookings ?? []).map(b => {
                  const ts = TYPE_STYLES[b.type] || {};
                  return (
                    <tr key={b.ref}>
                      <td><span className={styles.miniRef}>{b.ref}</span></td>
                      <td>
                        <span className={styles.miniTypeBadge} style={{ background: ts.bg, color: ts.color }}>
                          {TYPE_ICON[b.type]} {b.type}
                        </span>
                      </td>
                      <td className={styles.miniDate}>{b.firstService}</td>
                      <td className={styles.miniDate}>{b.lastService}</td>
                      <td><span className={styles.paxBadge}>{b.pax} pax</span></td>
                      <td><strong>€{b.totalPrice.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</strong></td>
                      <td>
                        <span className={`${styles.miniPayBadge} ${
                          b.paymentStatus === "Paid"    ? styles.payPaid    :
                          b.paymentStatus === "Partial" ? styles.payPartial : styles.payUnpaid}`}>
                          {b.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.miniStatusBadge} ${
                          b.bookingStatus === "Confirmed" ? styles.statusConfirmed :
                          b.bookingStatus === "Pending"   ? styles.statusPending   : styles.statusCancelled}`}>
                          {b.bookingStatus}
                        </span>
                      </td>
                      <td>
                        <button
                          className={styles.openBookingBtn}
                          onClick={() => navigate(`/bookings/${b.ref}`, { state: { booking: b, fromCompany: true } })}
                        >
                          Open →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Tab: Financials                                                 */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeTab === "financials" && (
        <div className={styles.tabContent}>
          <div className={styles.twoCol}>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>💰</span>
                <h3>Financial Summary</h3>
              </div>
              <InfoRow label="Total Spent (all time)"   value={`€${(c.financials?.totalSpent ?? 0).toLocaleString("en-EU", { minimumFractionDigits: 2 })}`} />
              <InfoRow label="Open Balance"   value={`€${(c.financials?.openBalance ?? 0).toLocaleString("en-EU", { minimumFractionDigits: 2 })}`} />
              <InfoRow label="Available Credit Notes"  value={`€${(c.financials?.creditNotes ?? 0).toLocaleString("en-EU", { minimumFractionDigits: 2 })}`} />
              <InfoRow label="Payment Terms"  value={c.financials?.paymentTerms} />
              <InfoRow label="Last Payment"  value={c.financials?.lastPayment && c.financials?.lastPaymentAmount != null ? `€${c.financials.lastPaymentAmount.toLocaleString("en-EU", { minimumFractionDigits: 2 })} on ${c.financials.lastPayment}` : undefined} />
              {(c.financials?.openBalance ?? 0) > 0 && (
                <div className={styles.balanceWarning}>
                  ⚠️ This company has an outstanding balance of{" "}
                  <strong>€{(c.financials?.openBalance ?? 0).toLocaleString("en-EU", { minimumFractionDigits: 2 })}</strong>
                </div>
              )}
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>🧾</span>
                <h3>Credit Notes</h3>
                <span className={styles.cardCount}>{(c.creditNotes ?? []).length} note{(c.creditNotes ?? []).length !== 1 ? "s" : ""}</span>
              </div>
              {(c.creditNotes ?? []).length === 0 ? (
                <div className={styles.emptyMini}>No credit notes</div>
              ) : (
                (c.creditNotes ?? []).map(cn => (
                  <div key={cn.ref} className={styles.creditNoteRow}>
                    <div className={styles.cnLeft}>
                      <div className={styles.cnRef}>{cn.ref}</div>
                      <div className={styles.cnReason}>{cn.reason}</div>
                      <div className={styles.cnDate}>{cn.date}</div>
                    </div>
                    <div className={styles.cnRight}>
                      <div className={styles.cnAmount}>€{cn.amount.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</div>
                      <span className={styles.cnStatus}>{cn.status}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Tab: Notes                                                      */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeTab === "notes" && (
        <div className={styles.tabContent}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>📝</span>
              <h3>Internal Notes</h3>
              <span className={styles.cardCount}>{localNotes.length} note{localNotes.length !== 1 ? "s" : ""}</span>
            </div>
            <div className={styles.addNoteBox}>
              <textarea
                className={styles.noteInput}
                placeholder="Add an internal note about this company..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={3}
              />
              <div className={styles.addNoteFooter}>
                <span className={styles.noteHint}>Visible only to staff</span>
                <button
                  className={styles.addNoteBtn}
                  disabled={!noteText.trim()}
                  onClick={() => {
                    setLocalNotes((prev) => [
                      {
                        id: Date.now(),
                        author: "Admin",
                        date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }),
                        text: noteText.trim(),
                      },
                      ...prev,
                    ]);
                    setNoteText("");
                  }}
                >
                  Add Note
                </button>
              </div>
            </div>
            <div className={styles.notesList}>
              {localNotes.map((n) => (
                <div key={n.id} className={styles.noteItem}>
                  <div className={styles.noteHeader}>
                    <div className={styles.noteAuthorBadge}>{n.author[0]}</div>
                    <div>
                      <span className={styles.noteAuthor}>{n.author}</span>
                      <span className={styles.noteDate}>{n.date}</span>
                    </div>
                  </div>
                  <p className={styles.noteText}>{n.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <ContactModal
        open={contactModal.open}
        onClose={closeContact}
        onSave={handleSaveContact}
        contact={contactModal.contact}
        companyName={c.companyName}
      />
      <AddProfessionalCustomerModal
        open={editModalOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        company={{
          ...c,
          primaryContact: (() => {
            const pc = primaryOverride ?? c.contacts?.[0];
            if (!pc) return c.primaryContact;
            return {
              name: `${pc.firstName ?? ""} ${pc.lastName ?? ""}`.trim() || "—",
              email: pc.email ?? "",
              phone: pc.phone ?? "",
              role: pc.role ?? "",
              language: pc.language ?? "",
            };
          })(),
        }}
        apiError={updateError}
      />

    </div>
  );
};

export default ProfessionalCustomerDetail;